package one.healthium.domain.user.models

import one.healthium.base.utils.BodyParamsUtils
import one.healthium.base.utils.toMeters
import one.healthium.domain.common.BMIRange
import one.healthium.domain.common.LoseWeightRange

val UserProfileModel.minRecommendedWeight: Float?
    get() = if (weight == null || height == null) {
        null
    } else {
        val heightInMeters = height.toMeters()
        heightInMeters * heightInMeters * 19
    }

val UserProfileModel.maxRecommendedWeight: Float?
    get() = if (weight == null || height == null) {
        null
    } else {
        val heightInMeters = height.toMeters()
        heightInMeters * heightInMeters * 35
    }

val UserProfileModel.bmi: Int?
    get() = if (weight == null || height == null || height == 0f) {
        null
    } else {
        BodyParamsUtils.bmi(weight, height)
    }

val UserProfileModel.bmiRange: BMIRange?
    get() = bmi?.let { BMIRange.byValue(it) }

val UserProfileModel.targetBmi: Int?
    get() = if (targetWeight == null || height == null || height == 0f) {
        null
    } else {
        BodyParamsUtils.bmi(targetWeight, height)
    }

val UserProfileModel.losePercent: Int?
    get() = if (targetWeight == null || weight == null || weight == 0f) {
        null
    } else {
        ((1 - (targetWeight / weight.toFloat())) * 100).toInt()
    }

val UserProfileModel.loseWeightRange: LoseWeightRange?
    get() = losePercent?.let { LoseWeightRange.byRange(it) }

// TODO loco
val UserProfileModel.yourTeens: String
    get() = if (age == null) {
        "your age"
    } else {
        when (age / 10) {
            2 -> "twenties"
            3 -> "thirties"
            4 -> "forties"
            5 -> "fifties"
            6 -> "sixties"
            7 -> "seventies"
            8 -> "eighties"
            9 -> "nineties"
            else -> "your age"
        }
    }

val UserProfileModel.weightToLose: Float?
    get() = if (weight == null || targetWeight == null) {
        null
    } else {
        weight - targetWeight
    }

val UserProfileModel.prognoseLose: Float?
    get() = run {
        val prognoseWeight = prognoseWeight21days
        if (weight == null || prognoseWeight == null) {
            null
        } else {
            weight - prognoseWeight.weight
        }
    }

private val UserProfileModel.prognoseWeight21days: PrognoseWeightModel?
    get() = prognoseWeightArray21days.lastOrNull()
private val UserProfileModel.prognoseWeight3month: PrognoseWeightModel?
    get() = prognoseWeightArray3month.lastOrNull()
private val UserProfileModel.prognoseWeight3monthShort: PrognoseWeightModel?
    get() = prognoseWeightArray3monthShort.lastOrNull()

private val UserProfileModel.prognoseWeightArray21days
    get() = run {
        if (weight != null) {
            weightArray(weight, PersonalPlanPeriod.In21days).mapIndexed { index, weight ->
                PrognoseWeightModel(
                    weight = weight,
                    inDays = index * 7,
                )
            }
        } else {
            emptyList()
        }
    }

private val UserProfileModel.prognoseWeightArray3month: List<PrognoseWeightModel>
    get() = if (weight != null) {
        weightArray(weight, PersonalPlanPeriod.In3month).mapIndexed { index, weight ->
            PrognoseWeightModel(
                weight = weight,
                inDays = index * 30,
            )
        }
    } else {
        emptyList()
    }

private val UserProfileModel.prognoseWeightArray3monthShort: List<PrognoseWeightModel>
    get() = if (weight != null) {
        weightArray(weight, PersonalPlanPeriod.In3monthShort).mapIndexed { index, weight ->
            PrognoseWeightModel(
                weight = weight,
                inDays = index * 25,
            )
        }
    } else {
        emptyList()
    }

private val UserProfileModel.prognoseWeightLoss21days: Float?
    get() = prognoseWeight21days?.weight

private val UserProfileModel.prognoseWeightLoss3Month: Float?
    get() = prognoseWeight3month?.weight

private val UserProfileModel.prognoseWeightLoss3MonthShort: Float?
    get() = prognoseWeight3monthShort?.weight

fun UserProfileModel.prognoseWeightLossPerDay(period: PersonalPlanPeriod?) = when (period) {
    PersonalPlanPeriod.In21days -> prognoseWeightLossPerDay21days
    PersonalPlanPeriod.In3month -> prognoseWeightLossPerDay3month
    PersonalPlanPeriod.In3monthShort -> prognoseWeightLossPerDay3monthShort
    null -> prognoseWeightLossPerDay21days
}

fun UserProfileModel.prognoseWeightLoss(period: PersonalPlanPeriod?) = when (period) {
    PersonalPlanPeriod.In3month -> prognoseWeightLoss3Month
    PersonalPlanPeriod.In3monthShort -> prognoseWeightLoss3MonthShort
    PersonalPlanPeriod.In21days -> prognoseWeightLoss21days
    null -> prognoseWeightLoss21days
}

private val UserProfileModel.prognoseDays21days: Int
    get() = prognoseWeight21days?.inDays ?: 21
private val UserProfileModel.prognoseDays3month: Int
    get() = prognoseWeight3month?.inDays ?: 90
private val UserProfileModel.prognoseDays3monthShort: Int
    get() = prognoseWeight3monthShort?.inDays ?: 75

fun UserProfileModel.prognoseDays(period: PersonalPlanPeriod?) = when (period) {
    PersonalPlanPeriod.In3month -> prognoseDays3month
    PersonalPlanPeriod.In3monthShort -> prognoseDays3monthShort
    PersonalPlanPeriod.In21days -> prognoseDays21days
    null -> prognoseDays21days
}

val UserProfileModel.prognoseWeightLossPerDay21days: Float?
    get() = run {
        val prognoseWeight = prognoseWeight21days
        if (weight == null || prognoseWeight == null) {
            null
        } else {
            (weight - prognoseWeight.weight) / prognoseWeight.inDays.toFloat()
        }
    }

private val UserProfileModel.prognoseWeightLossPerDay3month: Float?
    get() = run {
        val prognoseWeight = this.prognoseWeight3month
        if (weight == null || prognoseWeight == null) {
            null
        } else {
            (weight - prognoseWeight.weight) / prognoseWeight.inDays.toFloat()
        }
    }

private val UserProfileModel.prognoseWeightLossPerDay3monthShort: Float?
    get() = run {
        val prognoseWeight = this.prognoseWeight3monthShort
        if (weight == null || prognoseWeight == null) {
            null
        } else {
            (weight - prognoseWeight.weight) / prognoseWeight.inDays.toFloat()
        }
    }

fun UserProfileModel.prognoseWeightArray(period: PersonalPlanPeriod?): List<PrognoseWeightModel> {
    return when (period) {
        PersonalPlanPeriod.In3month -> prognoseWeightArray3month
        PersonalPlanPeriod.In3monthShort -> prognoseWeightArray3monthShort
        PersonalPlanPeriod.In21days -> prognoseWeightArray21days
        null -> prognoseWeightArray21days
    }
}

private fun weightArray(startWeight: Float, period: PersonalPlanPeriod): List<Float> {
    val result = mutableListOf<Float>()

    val firstPeriodPartLost: Int
    val secondPeriodPertLost: Int
    val thirdPeriodPartLost: Int

    when (period) {
        PersonalPlanPeriod.In3month,
        PersonalPlanPeriod.In3monthShort -> {
            firstPeriodPartLost = 8
            secondPeriodPertLost = 15
            thirdPeriodPartLost = 24
        }

        PersonalPlanPeriod.In21days -> {
            when (startWeight) {
                in 41.0..60.0 -> {
                    firstPeriodPartLost = 2
                    secondPeriodPertLost = 3
                    thirdPeriodPartLost = 4
                }

                in 61.0..80.0 -> {
                    firstPeriodPartLost = 2
                    secondPeriodPertLost = 4
                    thirdPeriodPartLost = 6
                }

                else -> {
                    firstPeriodPartLost = 2
                    secondPeriodPertLost = 5
                    thirdPeriodPartLost = 8
                }
            }
        }
    }

    val lbsInKg = 0.453592f

    result.add(startWeight)
    result.add(startWeight - (firstPeriodPartLost * lbsInKg))
    result.add(startWeight - (secondPeriodPertLost * lbsInKg))
    result.add(startWeight - (thirdPeriodPartLost * lbsInKg))

    return result
}
